export type EmptyObject = {
  [K in string | number]: never;
};

export function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
  const entries = keys.map((key) => [key, obj[key]]);
  return Object.fromEntries(entries);
}

/**
 * removes specified keys from object
 *
 * @see https://stackoverflow.com/a/67434028/2284240
 */
export function omit<T extends object, K extends keyof T>(obj: T, ...keys: K[]) {
  const returnVal = { ...obj };

  for (const key of keys) {
    delete returnVal[key];
  }
  return returnVal as unknown as DistributiveOmit<T, K>;
}

/**
 * Overrides keys in dest from source
 *
 * NOTE: It can handle objects with primitive values or a nested object with primitive values (recursive)
 *
 * @param dest object to be overridden
 * @param source overrides object
 * @returns dest object with overridden keys
 *
 * @example
 * const result = deepDefaults({ a: { b: 1 }, c: 2, d: [] }, { c: 3, d: [1,2] });
 * { a: { b: 1 }, c: 3, d: [1,2] }
 */
export function deepDefaults<T extends object>(dest: T, source: DeepPartial<T>): T {
  if (!dest) return source as T;

  dest = { ...dest };

  for (const key in source) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const value = source[key];
    if (Array.isArray(value)) {
      // @ts-ignore
      dest[key] = [...value];
    } else if (typeof value === 'object') {
      // @ts-ignore
      dest[key] = deepDefaults(dest[key], value);
    } else {
      // @ts-ignore
      dest[key] = value;
    }
  }

  return dest;
}

export function isEmpty<T extends object>(obj: T): boolean {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function serializeError(error: any) {
  if (!(error instanceof Error)) return error;

  const plainObject: Record<any, any> = {};
  Object.getOwnPropertyNames(error).forEach((_key) => {
    const key = _key as keyof typeof error;
    plainObject[key] = error[key];
  });
  return plainObject;
}
